import React, { useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import {
  watchIntakeEmails, watchIntakeReviews, watchDryRunRows, watchIntakeSettings, resolveIntakeReview,
} from "../../services/intake";
import JobDetailModal from "../../components/owner/JobDetailModal";
import JobCreateModal from "../../components/owner/JobCreateModal";
import SameRunPanel from "../../components/owner/SameRunPanel";
import { MalcaCard, MovesList, MalcaDryRun, isMalcaReview } from "../../components/owner/MalcaIntake";

// Email intake. Shows what intake would do with each Ferrari email (dry
// run), the review cards that need the owner, and the raw email log. The
// only writes are the review-card buttons, which go through the
// resolveIntakeReview callable. Design: docs/ferrari-intake-design.md §9.

const ACTION_CHIP = {
  create: { cls: "chip-green", label: "Would create job" },
  update: { cls: "chip-blue", label: "Would link to job" },
  review: { cls: "chip-amber", label: "Would need review" },
};

const REASON_LABEL = {
  missing_date: "Date not from the forwarder's note — confirm it",
  direction: "Direction unclear",
  direction_conflict: "Note and addresses disagree on direction",
  conflict: "Email disagrees with the job",
  ambiguous_match: "More than one job has this order number",
  unknown_format: "Order number not recognized",
  dispatch_for_cancelled: "Order is on a cancelled job",
  redispatch_after_complete: "New date for a completed job",
  parse_failed: "Couldn't read part of the email",
  quarantine: "Unknown or unverified sender",
  pod_confirm: "Signed POD received — confirm complete",
  low_confidence_pod: "POD order number not clearly readable",
  sanity_mismatch: "POD date doesn't fit the job",
  unexpected: "POD for an order with no job",
  pod_for_cancelled: "POD for a cancelled job",
  no_show: "Pickup marked no-show — cancel or bill it",
};

const POD_ACTION_CHIP = {
  attach: { cls: "chip-green", label: "Would attach POD" },
  review: { cls: "chip-amber", label: "Would need review" },
};

// "NYW600377561" or "NYW6003775G1 (low)" from a { value, confidence } read.
const readOf = (f) => (f && f.value ? `${f.value}${f.confidence !== "high" ? ` (${f.confidence})` : ""}` : "—");

// The signature that proves the run: consignee for deliveries, shipper on pickup copies.
const signerOf = (s) => (s?.signer?.value ? s.signer : s?.shipperSigner);
const signedDateOf = (s) => (s?.signedDate?.value ? s.signedDate : s?.shipperSignedDate);

function Thumbs({ urls }) {
  if (!urls?.length) return null;
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
      {urls.map((u) => (
        <a key={u} href={u} target="_blank" rel="noreferrer">
          <img src={u} alt="POD" style={{ width: 72, height: 72, objectFit: "cover", borderRadius: 6, border: "1px solid #ddd" }} />
        </a>
      ))}
    </div>
  );
}

const STATUS_CHIP = {
  stored: "chip-gray", classified: "chip-gray", ignored: "chip-gray",
  dry_run: "chip-purple", applied: "chip-green", needs_review: "chip-amber", error: "chip-red", pod_queued: "chip-blue",
  scan_queued: "chip-blue", unpacked: "chip-gray",
};
const STATUS_LABEL = { needs_review: "needs you", applied: "added" };

const fmtDate = (ymd) => {
  if (!ymd) return "—";
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
};
const fmtWhen = (ts) => (ts?.toDate ? ts.toDate().toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");

const cell = { padding: "8px 10px", borderBottom: "1px solid #eee", fontSize: 12, verticalAlign: "top" };
const head = { ...cell, fontSize: 10, fontWeight: 700, color: "#888", textTransform: "uppercase", background: "#fafafa" };

function Flag({ on, label }) {
  return <span className={`chip ${on ? "chip-green" : "chip-gray"}`} style={{ marginRight: 6 }}>{label}: {on ? "on" : "off"}</span>;
}

function ReviewList({ reviews }) {
  if (!reviews?.length) return null;
  return (
    <div style={{ marginTop: 4 }}>
      {reviews.map((r, i) => (
        <div key={i} style={{ fontSize: 11, color: "#856404" }}>
          ⚠ {REASON_LABEL[r.reason] || r.reason}
          {r.details?.conflicts?.map((c) => (
            <span key={c.field} style={{ color: "#555" }}> · {c.field}: job has {String(c.current)}, email says {String(c.proposed)}</span>
          ))}
          {r.details?.usedDate && <span style={{ color: "#555" }}> · used {fmtDate(r.details.usedDate)} ({String(r.details.source).replace(/_/g, " ")})</span>}
        </div>
      ))}
    </div>
  );
}

// Open review cards, one per job: two cards on the same job (the cancelled
// job that got both a scan and a POD) become one card whose buttons act on
// both. A POD confirm card keeps its own card — its buttons differ.
function groupReviews(reviews) {
  const groups = [];
  const byJob = new Map();
  for (const r of reviews) {
    // Malca cards each have their own buttons, so they never merge.
    const key = r.jobId && r.reason !== "pod_confirm" && !isMalcaReview(r) ? r.jobId : null;
    if (key && byJob.has(key)) { byJob.get(key).push(r); continue; }
    const g = [r];
    if (key) byJob.set(key, g);
    groups.push(g);
  }
  return groups;
}

// "a scan (DELIVERIES)" / "a POD (Charlotte)" for each email behind a card.
const KIND_WORD = { pod: "POD", scan: "scan", order_notice: "order notice" };
function sourcesOf(group, emailsById) {
  return group
    .map((r) => emailsById.get(r.emailId))
    .filter(Boolean)
    .map((e) => `${e.kind === "order_notice" ? "an" : "a"} ${KIND_WORD[e.kind] || "email"} (${String(e.subject || "no subject").replace(/^\s*((fwd?|re)\s*:\s*)+/i, "")})`);
}

// One review card with its actions. Every button goes through the
// resolveIntakeReview callable; the card disappears once it's closed.
// `group` holds every open review on the same job (usually just one).
function ReviewCard({ group, emailsById, isPhone, onOpenJob }) {
  const review = group[0];
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [date, setDate] = useState(review.details?.usedDate || review.summary?.scheduledDate || "");
  const dateConflict = (review.details?.conflicts || []).find((c) => c.field === "scheduledDate");
  const canSetDate = review.jobId && (review.reason === "missing_date" || review.reason === "conflict");
  const isPod = review.reason === "pod_confirm";
  const noDate = isPod && !signedDateOf(review.summary)?.value;
  const thumb = isPod ? review.details?.photoUrls?.[0] : null;
  const sources = group.length > 1 ? sourcesOf(group, emailsById) : [];
  const labels = [...new Set(group.map((r) => REASON_LABEL[r.reason] || r.reason))];

  async function act(action, value) {
    setBusy(true);
    setErr("");
    try {
      for (const r of group) {
        // eslint-disable-next-line no-await-in-loop
        await resolveIntakeReview(r.id, action, value);
      }
    } catch (e) {
      setErr(e?.message || String(e));
      setBusy(false);
    }
  }

  const btn = isPhone ? { flex: 1, minHeight: 40, fontSize: 13 } : undefined;
  return (
    <div style={{ background: "#fff", border: "1px solid #e3e6ea", borderRadius: 10, padding: "12px 14px", marginBottom: 8, display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
        {thumb && (
          <a href={thumb} target="_blank" rel="noreferrer">
            <img src={thumb} alt="POD" style={{ width: 46, height: 58, objectFit: "cover", borderRadius: 6, border: "1px solid #ddd", display: "block" }} />
          </a>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ fontWeight: 800, fontSize: 14.5 }}>{review.orderNo || review.details?.from || ""}</span>
            {isPod ? (
              noDate ? <span className="chip chip-amber">no date read</span>
                : review.checksPassed ? <span className="chip chip-green">✓ checks passed</span>
                  : <span className="chip chip-amber">check it</span>
            ) : labels.map((l) => (
              <span key={l} className={`chip ${/cancelled/i.test(l) ? "chip-red" : "chip-amber"}`}>{l}</span>
            ))}
            <span style={{ marginLeft: "auto", color: "#888", fontSize: 11 }}>{fmtWhen(review.createdAt)}</span>
          </div>
          <div style={{ fontSize: 12, color: "#6b7684", marginTop: 3 }}>
            {isPod && <>Signed POD{review.details?.subject || review.summary?.subject ? ` · "${review.details?.subject || review.summary?.subject}"` : ""} · job {fmtDate(review.details?.jobDate)}</>}
            {!isPod && review.summary?.localStop && <>{review.summary.localStop.name} · {fmtDate(review.summary.scheduledDate)}</>}
            {!isPod && !review.summary?.localStop && review.details?.subject}
          </div>
          {isPod && (
            <div style={{ fontSize: 12, color: "#6b7684" }}>
              Signed by {readOf(signerOf(review.summary))}
              {!noDate && <> on {readOf(signedDateOf(review.summary))} {review.summary?.signedTime?.value || ""}</>}
              {!review.checksPassed && (review.details?.why || []).length > 0 && (
                <span style={{ color: "#856404" }}> · check: {(review.details.why || []).join("; ")}</span>
              )}
            </div>
          )}
          {sources.length > 1 && (
            <div style={{ fontSize: 12, color: "#8a5a00", fontWeight: 600, marginTop: 3 }}>
              {sources.slice(0, -1).join(", ")} and {sources[sources.length - 1]} both came in for this job.
            </div>
          )}
        </div>
      </div>
      {(review.details?.conflicts || []).map((c) => (
        <div key={c.field} style={{ fontSize: 11, color: "#555" }}>
          {c.field}: job has <b>{String(c.current)}</b>, email says <b>{String(c.proposed)}</b>
        </div>
      ))}
      {isPod && review.details?.photoUrls?.length > 1 && <Thumbs urls={review.details.photoUrls.slice(1)} />}
      {["low_confidence_pod", "sanity_mismatch", "unexpected"].includes(review.reason) && (
        <div style={{ fontSize: 11, color: "#555" }}>
          Read: {readOf(review.details?.read || review.summary?.orderNumberRead)}
          {review.details?.podDate && <> · POD date {fmtDate(review.details.podDate)} vs job {fmtDate(review.details.jobDate)}</>}
          {review.candidates?.length > 0 && <> · possible jobs: {review.candidates.length}</>}
        </div>
      )}
      {typeof review.details?.why === "string" && (
        <div style={{ fontSize: 11, color: "#555" }}>{review.details.why}</div>
      )}
      {review.details?.usedDate && (
        <div style={{ fontSize: 11, color: "#555" }}>
          Used {fmtDate(review.details.usedDate)} from {String(review.details.source || "").replace(/_/g, " ")} — the forwarder's note had no date.
        </div>
      )}
      <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
        {isPod && <button className="btn btn-sm btn-green" style={btn} disabled={busy} onClick={() => act("confirm_complete")}>Confirm complete</button>}
        {review.jobId && <button className="btn btn-sm" style={btn} disabled={busy} onClick={() => onOpenJob(review.jobId)}>Open job</button>}
        {dateConflict && (
          <button className="btn btn-sm btn-blue" disabled={busy} onClick={() => act("use_email_date")}>
            Use email's date ({fmtDate(dateConflict.proposed)})
          </button>
        )}
        {canSetDate && (
          <>
            <input type="date" value={date} disabled={busy} onChange={(e) => setDate(e.target.value)}
              style={{ fontSize: 11, padding: "3px 6px", border: "1px solid #ddd", borderRadius: 6 }} />
            <button className="btn btn-sm" disabled={busy || !date} onClick={() => act("set_date", date)}>Set date</button>
          </>
        )}
        {group.some((r) => ["dispatch_for_cancelled", "pod_for_cancelled"].includes(r.reason)) && (
          <button className="btn btn-sm btn-green" style={btn} disabled={busy} onClick={() => act("restore_job")}>Restore job</button>
        )}
        {!isPod && <button className="btn btn-sm btn-blue" style={btn} disabled={busy} onClick={() => act("accept")}>Looks right</button>}
        <button className="btn btn-sm" style={btn} disabled={busy} onClick={() => act("dismiss")}>Dismiss</button>
        {err && <span style={{ fontSize: 11, color: "#a32d2d" }}>{err}</span>}
      </div>
    </div>
  );
}

// What intake would do with each order in a dry run (order notices or scans).
function OrderDryRun({ title, rows, emptyText }) {
  return (
    <div className="panel">
      <div className="panel-header">
        <div>
          <div className="panel-title">{title}</div>
          <div className="panel-subtitle">Nothing below has been written to jobs. Review these before going live.</div>
        </div>
        <span className="chip chip-purple">{rows.length} order{rows.length !== 1 ? "s" : ""}</span>
      </div>
      {rows.length === 0 ? (
        <div className="empty-state">{emptyText}</div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={head} align="left">Order #</th>
                <th style={head} align="left">Would do</th>
                <th style={head} align="left">Job date</th>
                <th style={head} align="left">Direction</th>
                <th style={head} align="left">Our stop</th>
                <th style={head} align="left">Pkg</th>
                <th style={head} align="left">Email</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const s = r.summary || {};
                const chip = ACTION_CHIP[r.action] || { cls: "chip-gray", label: r.action };
                return (
                  <tr key={r.id}>
                    <td style={{ ...cell, fontWeight: 700 }}>{s.orderNo || "—"}</td>
                    <td style={cell}>
                      <span className={`chip ${chip.cls}`}>{chip.label}</span>
                      {r.action === "update" && (
                        <div style={{ fontSize: 11, color: "#555", marginTop: 4 }}>
                          {r.jobNumber} · {r.patchFields?.length ? `fills ${r.patchFields.join(", ")}` : "no field changes"}
                        </div>
                      )}
                      <ReviewList reviews={r.reviews} />
                    </td>
                    <td style={cell}>
                      {fmtDate(s.scheduledDate)}
                      <div style={{ fontSize: 10, color: "#888" }}>{s.note || String(s.scheduledDateSource || "").replace(/_/g, " ")}</div>
                    </td>
                    <td style={cell}>
                      {s.jobType || "—"}
                      <div style={{ fontSize: 10, color: "#888" }}>{String(s.directionSource || "").replace(/_/g, " ")}</div>
                    </td>
                    <td style={cell}>
                      {s.localStop ? (
                        <>
                          <div style={{ fontWeight: 600 }}>{s.localStop.type}: {s.localStop.name}</div>
                          <div style={{ fontSize: 11, color: "#555" }}>{s.localStop.address}</div>
                        </>
                      ) : "—"}
                    </td>
                    <td style={cell}>{s.parcels ?? "—"} × {s.weightLbs != null ? `${s.weightLbs} lb` : "—"}</td>
                    <td style={{ ...cell, fontSize: 11, color: "#555" }}>
                      {r.from}<div>{fmtWhen(r.createdAt)}</div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// What intake would do with each signed POD in the dry run.
function PodDryRun({ rows }) {
  if (!rows.length) return null;
  return (
    <div className="panel">
      <div className="panel-header">
        <div>
          <div className="panel-title">POD dry run — what intake would do with signed PODs</div>
          <div className="panel-subtitle">Nothing below has been written to jobs.</div>
        </div>
        <span className="chip chip-purple">{rows.length} POD{rows.length !== 1 ? "s" : ""}</span>
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={head} align="left">Email</th>
              <th style={head} align="left">Order # read</th>
              <th style={head} align="left">Would do</th>
              <th style={head} align="left">Signer · date</th>
              <th style={head} align="left">Photos</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const s = r.summary || {};
              const chip = POD_ACTION_CHIP[r.action] || { cls: "chip-gray", label: r.action };
              return (
                <tr key={r.id}>
                  <td style={cell}><b>{r.subject || "(no subject)"}</b><div style={{ fontSize: 10, color: "#888" }}>{fmtWhen(r.createdAt)}</div></td>
                  <td style={cell}>{readOf(s.orderNumberRead)}</td>
                  <td style={cell}>
                    <span className={`chip ${chip.cls}`}>{chip.label}</span>
                    {r.action === "attach" && (
                      <div style={{ fontSize: 11, color: "#555", marginTop: 4 }}>
                        to {r.jobNumber} · {r.newState === "pod_received" ? "then you confirm complete" : "job already complete — photos only"}
                        {r.patchFields?.length ? ` · fills the job's POD photo` : ""}
                      </div>
                    )}
                    <ReviewList reviews={(r.reviews || []).filter((x) => x.reason !== "pod_confirm")} />
                    {(r.reviews || []).filter((x) => x.reason === "pod_confirm").map((x, i) => (
                      <div key={i} style={{ fontSize: 11, color: x.checksPassed ? "#2d7a2d" : "#856404" }}>
                        {x.checksPassed ? "✓ would pass all checks" : `check: ${(x.details?.why || []).join("; ")}`}
                      </div>
                    ))}
                  </td>
                  <td style={cell}>{readOf(signerOf(s))}<div style={{ fontSize: 11, color: "#555" }}>{readOf(signedDateOf(s))} {s.signedTime?.value || ""}</div></td>
                  <td style={cell}><Thumbs urls={r.photoUrls} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function EmailIntake() {
  const { userProfile } = useAuth();
  const companyId = userProfile?.companyId;
  const [settings, setSettings] = useState(null);
  const [rows, setRows] = useState([]);
  const [podRows, setPodRows] = useState([]);
  const [scanRows, setScanRows] = useState([]);
  const [bulk, setBulk] = useState({ busy: false, msg: "" });
  const [reviews, setReviews] = useState([]);
  const [emails, setEmails] = useState([]);
  const [error, setError] = useState("");
  const [openJobId, setOpenJobId] = useState(null);
  const [malcaRows, setMalcaRows] = useState([]);
  const [moves, setMoves] = useState([]);
  // Email text to open in Create Job (a Malca job to set up by hand).
  const [createFromEmail, setCreateFromEmail] = useState(null);
  const [showAllEmails, setShowAllEmails] = useState(false);
  const [showDryRuns, setShowDryRuns] = useState(false);
  const [isPhone, setIsPhone] = useState(typeof window !== "undefined" && window.innerWidth < 700);

  useEffect(() => {
    const onResize = () => setIsPhone(window.innerWidth < 700);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!companyId) return undefined;
    const onError = (err) => setError(err?.message || String(err));
    const unsubs = [
      watchIntakeSettings(companyId, setSettings),
      watchDryRunRows(companyId, setRows, onError),
      watchDryRunRows(companyId, setPodRows, onError, "m3-pods"),
      watchDryRunRows(companyId, setScanRows, onError, "m2-scans"),
      watchDryRunRows(companyId, setMalcaRows, onError, "m4-malca"),
      watchIntakeReviews(companyId, (r) => {
        setReviews(r.filter((x) => x.status === "open"));
        setMoves(r.filter((x) => x.reason === "malca_moved" && x.status === "info"));
      }, onError),
      watchIntakeEmails(companyId, setEmails, onError),
    ];
    return () => unsubs.forEach((u) => u && u());
  }, [companyId]);

  const confirmable = reviews.filter((r) => r.reason === "pod_confirm" && r.checksPassed);
  const groups = groupReviews(reviews);
  const emailsById = new Map(emails.map((e) => [e.id, e]));
  const allLive = settings?.enabled && settings?.live?.orderNotices && settings?.live?.scans && settings?.live?.pods;
  const malcaLive = settings?.live?.malca === true;
  // Once Malca is live its dry-run list moves into the folded history.
  const dryRunCount = rows.length + scanRows.length + podRows.length + (malcaLive ? malcaRows.length : 0);

  async function confirmAllPassed() {
    setBulk({ busy: true, msg: "" });
    let done = 0;
    const failed = [];
    for (const r of confirmable) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await resolveIntakeReview(r.id, "confirm_complete");
        done += 1;
      } catch (e) {
        failed.push(`${r.orderNo}: ${e?.message || e}`);
      }
    }
    setBulk({ busy: false, msg: `Confirmed ${done}${failed.length ? ` · failed: ${failed.join("; ")}` : ""}` });
  }

  const secHead = { fontSize: 11, fontWeight: 800, color: "#5b6675", letterSpacing: ".05em", textTransform: "uppercase", margin: "0 2px 8px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 };
  const foldBar = { width: "100%", background: "#fff", border: "1px solid #e3e6ea", borderRadius: 10, padding: "11px 14px", marginBottom: 8, display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 13.5, cursor: "pointer", textAlign: "left", fontFamily: "inherit" };

  const needsYou = (
    <div>
      <div style={secHead}>
        <span>Needs you · {groups.length}</span>
        <span style={{ display: "flex", gap: 8, alignItems: "center", textTransform: "none", letterSpacing: 0 }}>
          {bulk.msg && <span style={{ fontSize: 11, color: "#555", fontWeight: 400 }}>{bulk.msg}</span>}
          {confirmable.length > 0 && (
            <button className="btn btn-sm btn-green" disabled={bulk.busy} onClick={confirmAllPassed}>
              {bulk.busy ? "Confirming…" : `Confirm all that passed (${confirmable.length})`}
            </button>
          )}
        </span>
      </div>
      {groups.length === 0 ? (
        <div style={{ ...foldBar, cursor: "default", color: "#888" }}>Nothing waiting for you.</div>
      ) : (
        groups.map((g) => (isMalcaReview(g[0])
          ? <MalcaCard key={g[0].id} review={g[0]} isPhone={isPhone} onOpenJob={setOpenJobId} onCreateFromEmail={setCreateFromEmail} />
          : <ReviewCard key={g[0].id} group={g} emailsById={emailsById} isPhone={isPhone} onOpenJob={setOpenJobId} />))
      )}
    </div>
  );

  const recent = (
    <div>
      <div style={secHead}><span>Recent emails</span><span>{emails.length}</span></div>
      <div style={{ background: "#fff", border: "1px solid #e3e6ea", borderRadius: 10, padding: "2px 14px", marginBottom: 8 }}>
        {emails.length === 0 ? (
          <div style={{ padding: "12px 0", color: "#888", fontSize: 13 }}>No emails received yet.</div>
        ) : (
          <>
            {(showAllEmails ? emails : emails.slice(0, 6)).map((e) => (
              <div key={e.id} style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "8px 0", borderBottom: "1px solid #f0f2f5", alignItems: "center" }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 13.5, overflowWrap: "anywhere" }}>{e.subject || <span style={{ color: "#aaa" }}>(no subject)</span>}</div>
                  <div style={{ fontSize: 12, color: "#6b7684" }}>
                    {[KIND_WORD[e.kind] ? KIND_WORD[e.kind][0].toUpperCase() + KIND_WORD[e.kind].slice(1) : e.kind?.replace(/_/g, " "), String(e.from || "").replace(/@.*/, ""), fmtWhen(e.receivedAt)].filter(Boolean).join(" · ")}
                  </div>
                  {e.error && <div style={{ fontSize: 11, color: "#a32d2d" }}>{e.error}</div>}
                </div>
                <span className={`chip ${STATUS_CHIP[e.status] || "chip-gray"}`} style={{ flexShrink: 0 }}>{STATUS_LABEL[e.status] || String(e.status || "").replace(/_/g, " ")}</span>
              </div>
            ))}
            {emails.length > 6 && (
              <button onClick={() => setShowAllEmails((v) => !v)} style={{ width: "100%", background: "none", border: "none", color: "#185FA5", fontWeight: 800, fontSize: 13, padding: "9px 0", cursor: "pointer" }}>
                {showAllEmails ? "Show fewer" : `Show all ${emails.length}`}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );

  const dryRuns = dryRunCount > 0 && (
    <>
      <button style={{ ...foldBar, color: "#6b7684" }} onClick={() => setShowDryRuns((v) => !v)}>
        <span>Dry-run history · {dryRunCount} rows from testing</span>
        <span>{showDryRuns ? "Hide" : "Show ›"}</span>
      </button>
      {showDryRuns && (
        <>
          {malcaLive && <MalcaDryRun rows={malcaRows} live secHead={secHead} />}
          <OrderDryRun title="Dry run — order notices" rows={rows} emptyText="No order notices processed yet." />
          <OrderDryRun title="Dry run — scans and pre-alerts" rows={scanRows} emptyText="No scans or pre-alerts processed yet." />
          <PodDryRun rows={podRows} />
        </>
      )}
    </>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div className="page-header">
        <div>
          <div className="page-title">Email intake</div>
          {!isPhone && (
            <div className="page-subtitle">Ferrari emails sent to {settings?.addressToken ? `st-services-${settings.addressToken}@in.veloceflow.net` : "the intake address"}</div>
          )}
        </div>
        {allLive ? (
          <span style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
            <span className="chip chip-green" title="Ferrari: order notices, scans and PODs are all live">● Live</span>
            {!malcaLive && <span className="chip" style={{ background: "#f1e8fb", color: "#6b2fb3" }} title="Malca emails are read but nothing is written to jobs">Malca: dry run</span>}
          </span>
        ) : (
          <div>
            <Flag on={settings?.enabled} label="Intake" />
            <Flag on={settings?.live?.orderNotices} label="Order notices live" />
            <Flag on={settings?.live?.scans} label="Scans live" />
            <Flag on={settings?.live?.pods} label="PODs live" />
          </div>
        )}
      </div>

      <div className="page-body">
        {error && <div className="chip chip-red" style={{ marginBottom: 12 }}>Couldn't load intake data: {error}</div>}

        <div style={isPhone ? undefined : { display: "grid", gridTemplateColumns: "minmax(0, 1fr) 340px", gap: 16, alignItems: "start" }}>
          <div>
            {needsYou}
            <MovesList moves={moves} secHead={secHead} />
            <SameRunPanel companyId={companyId} onOpenJob={setOpenJobId} />
            {!malcaLive && <MalcaDryRun rows={malcaRows} live={false} secHead={secHead} />}
            {!isPhone && dryRuns}
          </div>
          <div>
            {recent}
            {isPhone && dryRuns}
          </div>
        </div>
      </div>
      {openJobId && (
        <JobDetailModal jobId={openJobId} companyId={companyId} onClose={() => setOpenJobId(null)} />
      )}
      {createFromEmail != null && (
        <JobCreateModal
          companyId={companyId}
          initialRawEmail={createFromEmail}
          onClose={() => setCreateFromEmail(null)}
          onCreated={() => setCreateFromEmail(null)}
        />
      )}
    </div>
  );
}
